import { parametrosDoObjeto } from './parametros'
import type { CenaTabletop, ObjetoTabletop, TipoObjetoTabletop, VetorTabletop } from './tipos'

/**
 * Terreno do table top: talude, barranco, vala, canal e cisterna.
 *
 * Cada peça de terreno é um objeto como os outros (seleciona, move, gira,
 * entra no histórico e no JSON), com a forma descrita por um perfil: uma linha
 * (z, altura) no referencial da peça, extrudada ao longo de X. Assim a cena 3D
 * desenha o perfil e o domínio calcula a altura do chão a partir da mesma lista
 * de pontos — o que se vê e o que se calcula não divergem.
 *
 * Limites desta primeira versão, de propósito:
 *  - só o giro em Y entra no cálculo da altura (peças de terreno não tombam);
 *  - a vala é um vão entre dois aterros: o chão da cena continua plano e o
 *    fundo da vala está na altura 0, as margens é que sobem. O canal e a
 *    cisterna fazem o contrário: são ESCAVADOS, com a rua no nível 0 e o fundo
 *    abaixo (altura negativa), e a cena abre um buraco no chão sobre eles;
 *  - os veículos acompanham a altura do terreno, mas não inclinam sobre a rampa.
 *
 * TypeScript puro: sem React e sem Three.js.
 */

export const TIPOS_TERRENO: readonly TipoObjetoTabletop[] = ['talude', 'barranco', 'vala', 'canal', 'cisterna']

export function ehTerreno(tipo: TipoObjetoTabletop): boolean {
  return TIPOS_TERRENO.includes(tipo)
}

/** Ponto do perfil: [z, altura] no referencial da peça, antes da escala. */
export type PontoPerfilTerreno = readonly [number, number]

export interface PerfilTerreno {
  /** Metade da extensão ao longo de X: a peça vai de -semiLarguraM a +semiLarguraM. */
  readonly semiLarguraM: number
  /** Superfície do terreno, de trás para a frente (z crescente). Começa e termina no chão, exceto a face de trás da rampa. */
  readonly pontos: readonly PontoPerfilTerreno[]
  /**
   * Terreno ESCAVADO (canal): o perfil fica abaixo do nível da rua (y ≤ 0) e a
   * cena abre um buraco no chão sobre ele. Os pontos cobrem só o vão e as
   * muretas; fora deles o chão é a rua, no nível 0.
   */
  readonly escavado?: boolean
  /** Só no escavado: a linha do vão, de borda a borda da rua, sem as muretas; é o que se desenha como paredes e leito. */
  readonly vazio?: readonly PontoPerfilTerreno[]
  /** Só no escavado: as muretas (faixas em Z com a altura acima da rua). */
  readonly muretas?: ReadonlyArray<{ readonly z0: number; readonly z1: number; readonly alturaM: number }>
}

/** Espessura da mureta no topo da parede do canal. */
export const ESPESSURA_MURETA_M = 0.25

/** Largura do aterro de cada lado da vala, no topo. */
export const MARGEM_VALA_M = 2.5
/** Quanto a parede interna da vala se afasta na horizontal por metro de profundidade (parede quase vertical). */
const RECUO_PAREDE_VALA = 0.15
/** Idem para o lado de fora do aterro, que desce suave até o chão. */
const RECUO_ENCOSTA_VALA = 1.5

const rad = (graus: number) => (graus * Math.PI) / 180

function perfilDaRampa(p: Record<string, number>): PerfilTerreno {
  const altura = p.alturaM!
  const subida = altura / Math.tan(rad(p.anguloGraus!))
  const total = subida + p.topoM!
  const pontos: PontoPerfilTerreno[] = [
    [-total / 2, 0],
    [-total / 2 + subida, altura],
  ]
  if (p.topoM! > 0) pontos.push([total / 2, altura])
  return { semiLarguraM: p.larguraM! / 2, pontos }
}

function perfilDaVala(p: Record<string, number>): PerfilTerreno {
  const h = p.profundidadeM!
  const meio = p.larguraM! / 2
  const parede = h * RECUO_PAREDE_VALA
  const encosta = h * RECUO_ENCOSTA_VALA
  const borda = meio + parede + MARGEM_VALA_M + encosta
  return {
    semiLarguraM: p.comprimentoM! / 2,
    pontos: [
      [-borda, 0],
      [-borda + encosta, h],
      [-(meio + parede), h],
      [-meio, 0],
      [meio, 0],
      [meio + parede, h],
      [borda - encosta, h],
      [borda, 0],
    ],
  }
}

/**
 * Canal em concreto, como o Ribeirão Arrudas: um vão ESCAVADO abaixo da rua. A
 * rua é o chão da cena, no nível 0 (e pode ter asfalto); o leito fica a
 * `alturaM` abaixo e a banqueta no pé da parede do lado negativo de Z, a
 * `alturaBanquetaM` acima do leito. A margem oposta pode ser uma parede ou uma
 * rampa de concreto. A origem é o centro do leito, no nível da rua.
 */
export interface MedidasDoCanal {
  /** Profundidade do canal: da rua ao leito. */
  readonly alturaM: number
  readonly meioLeitoM: number
  readonly banquetaM: number
  /** Altura da banqueta acima do leito, já presa abaixo do topo da parede. */
  readonly alturaBanquetaM: number
  /** Nível do leito, abaixo da rua (negativo). */
  readonly yLeitoM: number
  /** Nível da banqueta, abaixo da rua (negativo). */
  readonly yBanquetaM: number
  readonly muretaM: number
  /** Z da parede do lado da banqueta (a face da mureta, no topo). */
  readonly paredeZ: number
  /** Z do topo da margem oposta (parede ou fim da rampa). */
  readonly topoDireitoZ: number
}

export function medidasDoCanal(p: Record<string, number>): MedidasDoCanal {
  const alturaM = p.alturaParedeM!
  const meioLeitoM = p.larguraLeitoM! / 2
  const banquetaM = p.banquetaLarguraM!
  const recuoRampa = p.inclinacaoMargemGraus! >= 90 ? 0 : alturaM / Math.tan(rad(p.inclinacaoMargemGraus!))
  // a banqueta nunca chega ao topo da parede
  const alturaBanquetaM = Math.min(p.banquetaAlturaM!, alturaM - 0.3)
  return {
    alturaM,
    meioLeitoM,
    banquetaM,
    alturaBanquetaM,
    yLeitoM: -alturaM,
    yBanquetaM: -alturaM + alturaBanquetaM,
    muretaM: p.muretaAlturaM!,
    paredeZ: -(meioLeitoM + banquetaM),
    topoDireitoZ: meioLeitoM + recuoRampa,
  }
}

function perfilDoCanal(p: Record<string, number>): PerfilTerreno {
  const { meioLeitoM: meioLeito, banquetaM: banqueta, yLeitoM, yBanquetaM, muretaM: mureta, paredeZ: parede, topoDireitoZ: topoDireito } = medidasDoCanal(p)

  const lista = (inicio: PontoPerfilTerreno): { pontos: PontoPerfilTerreno[]; vai: (z: number, y: number) => void } => {
    const pontos: PontoPerfilTerreno[] = [inicio]
    return {
      pontos,
      vai: (z, y) => {
        const ultimo = pontos[pontos.length - 1]!
        if (ultimo[0] !== z || ultimo[1] !== y) pontos.push([z, y])
      },
    }
  }
  // a linha do vão: da borda da rua, pela parede, banqueta e leito, até a borda do outro lado
  const vazio = lista([parede, 0])
  if (banqueta > 0) {
    vazio.vai(parede, yBanquetaM)
    vazio.vai(-meioLeito, yBanquetaM)
  }
  vazio.vai(-meioLeito, yLeitoM)
  vazio.vai(meioLeito, yLeitoM)
  vazio.vai(topoDireito, 0)

  // a mesma linha com as muretas por cima, para a altura do chão
  const total = lista([parede - (mureta > 0 ? ESPESSURA_MURETA_M : 0), 0])
  if (mureta > 0) {
    total.vai(parede - ESPESSURA_MURETA_M, mureta)
    total.vai(parede, mureta)
  }
  for (const [z, y] of vazio.pontos) total.vai(z, y)
  if (mureta > 0) {
    total.vai(topoDireito, mureta)
    total.vai(topoDireito + ESPESSURA_MURETA_M, mureta)
    total.vai(topoDireito + ESPESSURA_MURETA_M, 0)
  }
  return {
    semiLarguraM: p.comprimentoM! / 2,
    pontos: total.pontos,
    escavado: true,
    vazio: vazio.pontos,
    muretas:
      mureta > 0
        ? [
            { z0: parede - ESPESSURA_MURETA_M, z1: parede, alturaM: mureta },
            { z0: topoDireito, z1: topoDireito + ESPESSURA_MURETA_M, alturaM: mureta },
          ]
        : [],
  }
}

/**
 * Cisterna: um poço redondo ESCAVADO no chão da rua. A boca fica no nível da
 * rua (y = 0) e o fundo `profundidadeM` abaixo. Com a tampa fechada a boca é
 * chão comum. A origem é o centro da boca.
 */
export interface FormaCisterna {
  readonly raioFuroM: number
  readonly profundidadeM: number
  readonly comTampa: boolean
  /** As paredes se desenham translúcidas, para ver o poço de qualquer lado (sem o corte). */
  readonly paredesTransparentes: boolean
  /**
   * Corte: a metade da frente (+Z do objeto) fica aberta, como num desenho em
   * corte, para ver o poço por dentro; o plano do corte mostra a parede e a
   * terra. Girar a cisterna escolhe o lado aberto. Só muda o desenho: o chão,
   * o furo e os pontos de ancoragem continuam os mesmos.
   */
  readonly meiaAberta: boolean
}

/** Espessura da parede de concreto da cisterna, que aparece no plano do corte. */
export const ESPESSURA_PAREDE_CISTERNA_M = 0.2
/** Quanto de terra em volta da parede o corte mostra, de cada lado. */
export const TERRA_NO_CORTE_CISTERNA_M = 1.2

export interface CorteDaCisterna {
  /** Metade da largura da vala de inspeção aberta na frente do corte (de -W a +W em X local). */
  readonly meiaLarguraM: number
  /** Até onde a vala vai para a frente (de z = 0 a este valor, em Z local). */
  readonly comprimentoM: number
  /** Profundidade do fundo da vala, abaixo da rua. */
  readonly fundoM: number
}

/**
 * A vala de inspeção do corte: na frente do plano do corte (z ≥ 0 local), o
 * chão se abre até um pouco abaixo do fundo do poço, para a câmera, mesmo de
 * cima, enxergar o poço por dentro. Comprida como o poço é fundo, para um olhar
 * a 45° alcançar o fundo.
 */
export function corteDaCisterna(forma: FormaCisterna): CorteDaCisterna {
  return {
    meiaLarguraM: forma.raioFuroM + ESPESSURA_PAREDE_CISTERNA_M + TERRA_NO_CORTE_CISTERNA_M,
    comprimentoM: forma.profundidadeM + 0.5,
    fundoM: forma.profundidadeM + ESPESSURA_PAREDE_CISTERNA_M + 0.6,
  }
}

export function formaDaCisterna(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): FormaCisterna | null {
  if (objeto.tipo !== 'cisterna') return null
  const p = parametrosDoObjeto(objeto)
  return {
    raioFuroM: p.diametroM! / 2,
    profundidadeM: p.profundidadeM!,
    comTampa: p.comTampa! >= 0.5,
    paredesTransparentes: p.paredesTransparentes! >= 0.5,
    meiaAberta: p.meiaAberta! >= 0.5,
  }
}

/**
 * As "ilhas" de um perfil: cada trecho que sobe do chão e volta a ele é um
 * sólido à parte. Um perfil com um vão no chão (vala, canal) tem duas; uma
 * rampa tem uma. Desenhar cada ilha como polígono simples evita que a base
 * sobreponha o vão, o que quebraria a triangulação da malha.
 */
export function ilhasDoPerfil(pontos: readonly PontoPerfilTerreno[]): ReadonlyArray<readonly PontoPerfilTerreno[]> {
  const ilhas: PontoPerfilTerreno[][] = []
  let atual: PontoPerfilTerreno[] = []
  const temAltura = (lista: readonly PontoPerfilTerreno[]) => lista.some((p) => p[1] > 0)
  for (const ponto of pontos) {
    if (ponto[1] === 0) {
      if (temAltura(atual)) {
        atual.push(ponto)
        ilhas.push(atual)
      }
      atual = [ponto]
    } else {
      atual.push(ponto)
    }
  }
  // um perfil que termina no alto (a face de trás da rampa) fecha caindo ao chão
  if (temAltura(atual)) ilhas.push(atual)
  return ilhas
}

/** O perfil do terreno, ou `null` se o objeto não é uma peça de terreno em extrusão (a cisterna é redonda: ver `formaDaCisterna`). */
export function perfilDoTerreno(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): PerfilTerreno | null {
  switch (objeto.tipo) {
    case 'talude':
    case 'barranco':
      return perfilDaRampa(parametrosDoObjeto(objeto))
    case 'vala':
      return perfilDaVala(parametrosDoObjeto(objeto))
    case 'canal':
      return perfilDoCanal(parametrosDoObjeto(objeto))
    default:
      return null
  }
}

function alturaNoPerfil(pontos: readonly PontoPerfilTerreno[], z: number): number {
  const ultimo = pontos[pontos.length - 1]!
  for (let i = 1; i < pontos.length; i += 1) {
    const [z0, y0] = pontos[i - 1]!
    const [z1, y1] = pontos[i]!
    if (z > z1 || z1 === z0) continue
    return y0 + ((y1 - y0) * (z - z0)) / (z1 - z0)
  }
  return ultimo[1]
}

/** Leva um ponto do mundo para o referencial da peça (só o giro em Y entra), já dividido pela escala. */
function paraLocal(objeto: ObjetoTabletop, x: number, z: number): { lx: number; lz: number } {
  const { posicao, rotacao, escala } = objeto.transformacao
  const dx = x - posicao[0]
  const dz = z - posicao[2]
  // inverso do giro em Y que a cena aplica ao objeto
  const cos = Math.cos(rotacao[1])
  const sen = Math.sin(rotacao[1])
  return { lx: (dx * cos - dz * sen) / escala[0], lz: (dx * sen + dz * cos) / escala[2] }
}

/**
 * Altura da superfície de uma peça de terreno no ponto (x, z) do mundo, já com
 * a altura em que a peça foi apoiada; `null` onde a peça não cobre o ponto (ali
 * o chão é o de sempre). Pode ser NEGATIVA: no canal e na cisterna o chão é
 * escavado abaixo da rua.
 */
export function alturaDoObjetoTerrenoEm(objeto: ObjetoTabletop, x: number, z: number): number | null {
  const cisterna = formaDaCisterna(objeto)
  const perfil = cisterna ? null : perfilDoTerreno(objeto)
  if (!cisterna && !perfil) return null
  const { posicao, escala } = objeto.transformacao
  const { lx, lz } = paraLocal(objeto, x, z)
  if (cisterna) {
    // com a tampa fechada a boca é chão comum
    if (cisterna.comTampa) return null
    if (Math.hypot(lx, lz) < cisterna.raioFuroM) return posicao[1] - cisterna.profundidadeM * escala[1]
    // em corte, a vala de inspeção na frente do corte também é chão (o fundo dela)
    if (cisterna.meiaAberta) {
      const c = corteDaCisterna(cisterna)
      if (lz >= 0 && lz <= c.comprimentoM && Math.abs(lx) <= c.meiaLarguraM) return posicao[1] - c.fundoM * escala[1]
    }
    return null
  }
  if (!perfil || Math.abs(lx) > perfil.semiLarguraM) return null
  const primeiro = perfil.pontos[0]!
  const ultimo = perfil.pontos[perfil.pontos.length - 1]!
  if (lz < primeiro[0] || lz > ultimo[0]) return null
  const altura = alturaNoPerfil(perfil.pontos, lz)
  // num terreno elevado, altura zero é o pé da rampa: já é o chão comum
  if (altura === 0 && !perfil.escavado) return null
  return posicao[1] + altura * escala[1]
}

/** Altura do chão no ponto (x, z): a do terreno mais alto que o cobre, ou 0 no chão plano. `ignorarId` deixa de fora uma peça. */
export function alturaDoTerrenoEm(cena: CenaTabletop, x: number, z: number, ignorarId?: string): number {
  return alturaDosObjetosEm(cena.objetos, x, z, ignorarId)
}

/** O mesmo que `alturaDoTerrenoEm`, para quem tem só a lista de objetos (a cena 3D). */
export function alturaDosObjetosEm(
  objetos: readonly ObjetoTabletop[],
  x: number,
  z: number,
  ignorarId?: string,
): number {
  let altura: number | null = null
  for (const objeto of objetos) {
    if (objeto.oculto || objeto.id === ignorarId || !ehTerreno(objeto.tipo)) continue
    const h = alturaDoObjetoTerrenoEm(objeto, x, z)
    if (h !== null) altura = altura === null ? h : Math.max(altura, h)
  }
  return altura ?? 0
}

/**
 * Os buracos que as peças escavadas abrem no chão da rua, como polígonos de
 * pontos (x, z) no mundo. A cena desenha o asfalto com estes vazados, para se
 * ver o canal e a cisterna por dentro.
 */
export function buracosDoTerreno(objeto: ObjetoTabletop): ReadonlyArray<ReadonlyArray<readonly [number, number]>> {
  if (objeto.oculto) return []
  const { posicao, rotacao, escala } = objeto.transformacao
  const cos = Math.cos(rotacao[1])
  const sen = Math.sin(rotacao[1])
  // giro em Y da peça (local -> mundo), depois a posição
  const mundo = (lx: number, lz: number): readonly [number, number] => {
    const x = lx * escala[0]
    const z = lz * escala[2]
    return [posicao[0] + x * cos + z * sen, posicao[2] - x * sen + z * cos]
  }
  const cisterna = formaDaCisterna(objeto)
  if (cisterna) {
    if (cisterna.comTampa) return []
    const lados = 48
    if (cisterna.meiaAberta) {
      // buraco em fechadura: a metade de trás do furo (z < 0) e a vala aberta na frente do corte
      const c = corteDaCisterna(cisterna)
      const R = cisterna.raioFuroM
      const arco = Array.from({ length: lados / 2 + 1 }, (_, i) => {
        const a = Math.PI + (Math.PI * i) / (lados / 2) // de -X, por trás (z < 0), até +X
        return mundo(Math.cos(a) * R, Math.sin(a) * R)
      })
      return [[mundo(-c.meiaLarguraM, c.comprimentoM), mundo(-c.meiaLarguraM, 0), ...arco, mundo(c.meiaLarguraM, 0), mundo(c.meiaLarguraM, c.comprimentoM)]]
    }
    return [
      Array.from({ length: lados }, (_, i) => {
        const a = (2 * Math.PI * i) / lados
        return mundo(Math.cos(a) * cisterna.raioFuroM, Math.sin(a) * cisterna.raioFuroM)
      }),
    ]
  }
  const perfil = perfilDoTerreno(objeto)
  if (!perfil?.escavado || !perfil.vazio) return []
  const z0 = perfil.vazio[0]![0]
  const z1 = perfil.vazio[perfil.vazio.length - 1]![0]
  const s = perfil.semiLarguraM
  return [[mundo(-s, z0), mundo(s, z0), mundo(s, z1), mundo(-s, z1)]]
}

const PASSO_NORMAL_M = 0.05

/** Normal unitária da superfície no ponto (x, z), por diferença finita da altura. Chão plano: (0, 1, 0). */
export function normalDoTerrenoEm(cena: CenaTabletop, x: number, z: number, ignorarId?: string): VetorTabletop {
  const h = (px: number, pz: number) => alturaDoTerrenoEm(cena, px, pz, ignorarId)
  const gx = (h(x + PASSO_NORMAL_M, z) - h(x - PASSO_NORMAL_M, z)) / (2 * PASSO_NORMAL_M)
  const gz = (h(x, z + PASSO_NORMAL_M) - h(x, z - PASSO_NORMAL_M)) / (2 * PASSO_NORMAL_M)
  if (gx === 0 && gz === 0) return [0, 1, 0]
  const norma = Math.hypot(gx, 1, gz)
  return [-gx / norma, 1 / norma, -gz / norma]
}

/** Até quanto o objeto pode estar longe da superfície e ainda contar como "apoiado" nela. */
const TOLERANCIA_ASSENTO_M = 0.02

function podeAssentar(objeto: ObjetoTabletop): boolean {
  if (ehTerreno(objeto.tipo)) return false
  // vítima sentada no carro vai onde o carro vai; a posição dela não é do chão
  if (objeto.vitima?.local.tipo === 'veiculo') return false
  return true
}

/**
 * Objetos apoiados em cima desta peça de terreno: dentro da pegada, acima do
 * chão e na altura da superfície. É o que anda junto quando a peça é movida.
 */
export function objetosApoiadosNoTerreno(cena: CenaTabletop, idTerreno: string): readonly string[] {
  const terreno = cena.objetos.find((o) => o.id === idTerreno)
  if (!terreno || !ehTerreno(terreno.tipo)) return []
  return cena.objetos
    .filter((o) => {
      if (!podeAssentar(o)) return false
      const h = alturaDoObjetoTerrenoEm(terreno, o.transformacao.posicao[0], o.transformacao.posicao[2])
      return h !== null && Math.abs(h) > 0.001 && Math.abs(o.transformacao.posicao[1] - h) <= TOLERANCIA_ASSENTO_M
    })
    .map((o) => o.id)
}

/**
 * Compara duas versões da cena e diz a que altura cada objeto que estava
 * apoiado no chão ou no terreno deve ficar depois da mudança. Só devolve quem
 * realmente muda; quem estava no ar (fora da tolerância) não é tocado.
 */
export function alturasDeReassentamento(
  antes: CenaTabletop,
  depois: CenaTabletop,
  ignorar: ReadonlySet<string> = new Set(),
): ReadonlyArray<{ readonly id: string; readonly y: number }> {
  const ajustes: Array<{ id: string; y: number }> = []
  for (const objeto of depois.objetos) {
    if (ignorar.has(objeto.id) || !podeAssentar(objeto)) continue
    const [x, y, z] = objeto.transformacao.posicao
    const antiga = alturaDoTerrenoEm(antes, x, z)
    if (Math.abs(y - antiga) > TOLERANCIA_ASSENTO_M) continue
    const nova = alturaDoTerrenoEm(depois, x, z)
    if (Math.abs(nova - antiga) < 1e-9) continue
    ajustes.push({ id: objeto.id, y: nova })
  }
  return ajustes
}
